import type {
  ClientPortalIntegrationItem,
  ClientRunStatus,
  IntegrationDetail,
} from "./integrationsBeta";

/** The last run as the header shows it: when it ran and its status in the
 * vocabulary the viewer may see. */
export interface IntegrationHeaderLastRun {
  id: number;
  started_at: string;
  /** Internal engine status - present for internal users only. */
  status?: string;
  /** Client-facing status - present on the client portal payload. */
  client_status?: ClientRunStatus | null;
}

/**
 * What the integration details header renders, built from either payload:
 * the internal detail (every internal role) or the client portal item (a
 * client user, or an admin previewing one in Client View). The header reads
 * this adapter only, so one component serves both without knowing which
 * endpoint answered (CAL-5777).
 */
export interface IntegrationHeaderData {
  id: number;
  client_name: string;
  name: string;
  /** null = not available to this viewer (the client payload carries none). */
  description: string | null;
  timezone: string;
  timezone_abbreviation: string;
  source_services: string[];
  target_services: string[];
  frequency: string | null;
  /** Cron expressions behind `frequency`, for re-rendering in another zone. */
  active_schedule_crons: string[];
  next_run_at: string | null;
  last_run: IntegrationHeaderLastRun | null;
}

export function headerDataFromDetail(
  detail: IntegrationDetail,
): IntegrationHeaderData {
  return {
    id: detail.id,
    client_name: detail.client_name,
    name: detail.name,
    description: detail.description,
    timezone: detail.timezone,
    timezone_abbreviation: detail.timezone_abbreviation,
    source_services: detail.source_services,
    target_services: detail.target_services,
    frequency: detail.frequency,
    active_schedule_crons: detail.schedules
      .filter((schedule) => schedule.is_active)
      .map((schedule) => schedule.cron_expression),
    next_run_at: detail.next_run_at,
    last_run: detail.last_run
      ? {
          id: detail.last_run.id,
          started_at: detail.last_run.started_at,
          status: detail.last_run.status,
        }
      : null,
  };
}

export function headerDataFromClientPortal(
  item: ClientPortalIntegrationItem,
): IntegrationHeaderData {
  return {
    id: item.id,
    client_name: item.client_name,
    name: item.name,
    description: null,
    timezone: item.timezone,
    timezone_abbreviation: item.timezone_abbreviation,
    source_services: item.source_services,
    target_services: item.target_services,
    frequency: item.frequency,
    active_schedule_crons: item.active_schedule_crons,
    next_run_at: item.next_run_at,
    last_run:
      item.last_run_id !== null && item.last_run_started_at
        ? {
            id: item.last_run_id,
            started_at: item.last_run_started_at,
            client_status: item.last_run_client_status,
          }
        : null,
  };
}
